import { Content, NewLiveclass, Res, Tag } from '@/types'
import { removeNullProps } from '@/utils'

import base from './base'

export const contentApi = base.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    addContent: builder.mutation<Content, FormData>({
      query: (body) => ({
        url: 'teachers/contents',
        method: 'POST',
        body,
      }),
      transformResponse: (res: Res<Content>) => res.data,
      invalidatesTags: (_, error) => (!error ? ['Content'] : []),
    }),

    getContents: builder.query<Content[], void>({
      query: () => 'teachers/contents',
      transformResponse: (res: Res<Content[]>) => res.data,
      providesTags: ['Content'],
    }),

    updateContents: builder.mutation<void, Partial<Content> & { contentTags: number[] }>({
      query: (data) => ({
        url: `teachers/contents/${data.id}`,
        method: 'PUT',
        body: removeNullProps({ ...data, tags: data.contentTags }),
      }),
      invalidatesTags: (_, error) => (!error ? ['Content'] : []),
    }),

    deleteContent: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `teachers/contents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['Content'] : []),
    }),

    addLiveclass: builder.mutation<void, NewLiveclass>({
      query: (body) => ({
        url: 'zoom/meetings',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.teaching_id }] : [],
    }),

    deleteLiveclass: builder.mutation<void, { teaching_id: string; meeting_id: string }>({
      query: ({ meeting_id }) => ({
        url: `zoom/meetings/${meeting_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.teaching_id }] : [],
    }),

    addAsyncLiveclass: builder.mutation<
      void,
      { teaching_id: number; recorded_url: string; duration?: number }
    >({
      query: (body) => ({
        url: 'zoom/async-meetings',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.teaching_id }] : [],
    }),
    deleteAsyncLiveclass: builder.mutation<
      void,
      { teaching_id: string | number; meeting_id: string | number }
    >({
      query: ({ meeting_id }) => ({
        url: `zoom/async-meetings/${meeting_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, args) =>
        !error ? [{ type: 'Teaching' as const, id: args.teaching_id }] : [],
    }),

    getTags: builder.query<Tag[], void>({
      query: () => 'teachers/tags',
      transformResponse: (res: Res<Tag[]>) => res.data,
      providesTags: ['Tags'],
    }),

    getContentResourceUrl: builder.query<Content, string | number>({
      query: (id) => `teachers/contents/${id}/resource`,
      transformResponse: (res: Res<Content>) => res.data,
    }),
  }),
})

export const {
  useGetContentsQuery,
  useUpdateContentsMutation,
  useAddLiveclassMutation,
  useDeleteLiveclassMutation,
  useAddContentMutation,
  useDeleteContentMutation,
  useGetTagsQuery,
  useLazyGetContentResourceUrlQuery,
  useAddAsyncLiveclassMutation,
  useDeleteAsyncLiveclassMutation,
} = contentApi
